'use client';
import { ReactNode, useEffect, useState } from 'react';
import { useAuth } from '@/components/context/auth-context';
import { useRouter } from 'next/navigation';
import useRedirect from '@/libs/hooks/use-redirect';

interface Props {
  requiredAuth?: boolean;
  requiredAccount?: boolean;
  notRequiredAuth?: boolean;
  notRequiredAccount?: boolean;
  children?: ReactNode;
}

const PATH_SIGN_IN = '/auth/sign-in';
const PATH_ACCOUNT_CREATE = '/auth/account/create';
const PATH_TERMS = '/terms';

export default function AuthGuard(props: Props) {
  const {
    requiredAuth = true,
    requiredAccount = true,
    notRequiredAuth = false,
    notRequiredAccount = false,
  } = props;
  const auth = useAuth();
  const router = useRouter();
  const redirect = useRedirect();

  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted || auth.isLoading) return;
    // 현재 URL 저장
    const currentPath = window.location.pathname + window.location.search;

    if(notRequiredAuth && !!auth.me) {
      const redirectUrl = redirect.get();
      redirect.clear();
      router.replace(redirectUrl || '/');
      return;
    }

    if(notRequiredAccount && !!auth.me && auth.me.activeAccount) {
      const redirectUrl = redirect.get();
      redirect.clear();
      router.replace(redirectUrl || '/');
      return;
    };

    if (!notRequiredAuth && requiredAuth && !auth.me) {
      // 로그인이 필요한 페이지인 경우 현재 URL 저장 후 로그인 화면으로
      if (currentPath === PATH_SIGN_IN) return;
      redirect.set(currentPath);
      router.replace(PATH_SIGN_IN);
      return;
    }

    if (!notRequiredAccount && requiredAccount && auth.me && !auth.me.activeAccount) {
      // 계정이 필요한 화면인 경우 현재 URL 저장 후 계정 생성 화면으로
      if (currentPath === PATH_ACCOUNT_CREATE) return;
      redirect.set(currentPath);
      router.replace(PATH_ACCOUNT_CREATE);
      return;
    }

    if (auth.me && !auth.me.isRequiredTermsAgreed) {
      // 필수 약관 동의가 필요한 경우 현재 URL 저장 후 약관 동의 화면으로
      if (currentPath === PATH_TERMS) return;
      redirect.set(currentPath);
      router.replace(PATH_TERMS);
      return;
    }
  }, [isMounted, auth.me, auth.isLoading, requiredAuth, requiredAccount, notRequiredAccount, notRequiredAuth, router, redirect]);

  const { children } = props;
  return <>
    {children}
  </>;
}
